import React, { useMemo, useState } from 'react';
import styles from '../../styles/AdminPanel.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import { downloadExcel } from '../../utils/exportExcel';

const STATUS_OPTIONS = [
  { id: 'new', label: 'Новий' },
  { id: 'processing', label: 'В обробці' },
  { id: 'shipped', label: 'Відправлено' },
  { id: 'completed', label: 'Виконано' },
  { id: 'cancelled', label: 'Скасовано' },
];

const FILTER_OPTIONS = [
  { id: 'all', label: 'Усі' },
  { id: 'new', label: 'Нові' },
  { id: 'processing', label: 'В обробці' },
  { id: 'shipped', label: 'Відправлені' },
  { id: 'completed', label: 'Виконані' },
  { id: 'cancelled', label: 'Скасовані' },
  { id: 'paid', label: 'Оплачені' },
  { id: 'unpaid', label: 'Неоплачені' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart}, ${timePart}`;
};

const getTotalQty = (items) => items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

const getCarrierIcon = (carrier) => {
  const c = String(carrier || '').toLowerCase();
  if (c.includes('нова')) return '📦';
  if (c.includes('укр')) return '✉️';
  return '🚚';
};

const getDeliveryBadge = (status) => {
  switch (status) {
    case 'shipped':
      return { text: 'На шляху до отримувача', tone: 'orange' };
    case 'processing':
      return { text: 'В обробці', tone: 'blue' };
    default:
      return null;
  }
};

const formatDeliveryAddress = (order) => {
  const city = order.citi || '';
  const department = order.department || '';
  if (city && department) return `${city}, отд. ${department}`;
  return city || department || '';
};

const exportOrdersExcel = (items) => {
  const headers = [
    'Номер замовлення',
    'Прізвище имя',
    'Номер телефону',
    'Замовлений товар',
    'Количество',
    'Ціна',
    'Перевізник',
    'Форма оплати',
    'Адреса доставки',
  ];
  const rows = [];
  items.forEach((order) => {
    const orderNumber = order.id;
    const fullName = `${order.lastName || ''} ${order.firstName || ''}`.trim();
    const phone = order.telephone || '';
    const carrier = order.carrier || '';
    const payment = order.payment || '';
    const address = formatDeliveryAddress(order);
    const orderItems = Array.isArray(order.items) && order.items.length ? order.items : [null];

    orderItems.forEach((item) => {
      rows.push([
        orderNumber,
        fullName,
        phone,
        item?.title || '',
        item ? (Number(item.quantity) || 1) : '',
        item ? (Number(item.price) || 0) : '',
        carrier,
        payment,
        address,
      ]);
    });
  });
  downloadExcel(`orders-${new Date().toISOString().slice(0, 10)}`, 'Замовлення', headers, rows);
};

const OrdersList = ({
  orders,
  loading,
  error,
  onEdit,
  onDelete,
  onStatusChange,
  onRefresh,
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [statusUpdating, setStatusUpdating] = useState({});

  const stats = useMemo(() => {
    const total = orders.length;
    const byStatus = STATUS_OPTIONS.reduce((acc, s) => {
      acc[s.id] = orders.filter((o) => (o.status || 'new') === s.id).length;
      return acc;
    }, {});
    return { total, ...byStatus };
  }, [orders]);

  const filterCounts = useMemo(() => {
    const counts = { all: orders.length };
    FILTER_OPTIONS.forEach((opt) => {
      if (opt.id === 'all') return;
      counts[opt.id] = orders.filter((order) => {
        switch (opt.id) {
          case 'paid':
            return Boolean(order.isPaid);
          case 'unpaid':
            return !order.isPaid;
          default:
            return (order.status || 'new') === opt.id;
        }
      }).length;
    });
    return counts;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    return orders.filter((order) => {
      const items = order.items || [];
      const itemTitles = items.map((i) => i.title).join(' ');
      const matchesSearch = !query || [
        String(order.id),
        order.firstName,
        order.lastName,
        order.telephone,
        order.User?.email,
        order.trackingNumber,
        itemTitles,
        order.citi,
        order.department,
      ].some((v) => String(v || '').toLowerCase().includes(query));

      if (!matchesSearch) return false;

      switch (filter) {
        case 'new':
        case 'processing':
        case 'shipped':
        case 'completed':
        case 'cancelled':
          return (order.status || 'new') === filter;
        case 'paid':
          return Boolean(order.isPaid);
        case 'unpaid':
          return !order.isPaid;
        default:
          return true;
      }
    });
  }, [orders, search, filter]);

  const allSelected = filteredOrders.length > 0 && selected.length === filteredOrders.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(filteredOrders.map((o) => o.id));
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const handleExport = () => {
    const items = selected.length
      ? filteredOrders.filter((o) => selected.includes(o.id))
      : filteredOrders;
    exportOrdersExcel(items);
  };

  const handleStatusChange = async (order, newStatus) => {
    if (!onStatusChange || newStatus === order.status) return;
    setStatusUpdating((prev) => ({ ...prev, [order.id]: true }));
    try {
      await onStatusChange(order.id, { status: newStatus });
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [order.id]: false }));
    }
  };

  const handleAddTracking = async (order) => {
    const tn = window.prompt('Введіть номер накладної (ТТН):', order.trackingNumber || '');
    if (tn === null || !onStatusChange) return;
    setStatusUpdating((prev) => ({ ...prev, [order.id]: true }));
    try {
      await onStatusChange(order.id, {
        status: order.status || 'new',
        trackingNumber: tn.trim(),
      });
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [order.id]: false }));
    }
  };

  const renderOrderItemCell = (item, order, isSubRow = false) => (
    <div className={isSubRow ? styles.ordersTableOrderSub : styles.ordersTableOrderMain}>
      <div className={styles.ordersTablePreviewBox}>
        <img
          src={assetUrl(item?.images?.[0] || '')}
          alt={item?.title || 'Товар'}
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
      </div>
      <div className={styles.ordersTableOrderInfo}>
        {!isSubRow && (
          <>
            <button type="button" className={styles.ordersTableOrderLink} onClick={() => onEdit?.(order)}>
              № {order.id}
            </button>
            <div className={styles.ordersTableOrderDate}>{formatDateTime(order.createdAt)}</div>
          </>
        )}
        <div className={styles.ordersTableOrderTitle}>{item?.title || 'Без назви'}</div>
      </div>
    </div>
  );

  const renderAmountCell = (order, item = null) => {
    if (item) {
      const qty = Number(item.quantity) || 1;
      const lineTotal = (Number(item.price) || 0) * qty;
      return (
        <div className={styles.ordersTableAmount}>
          <strong>{lineTotal} ₴</strong>
          <span>{qty} шт.</span>
        </div>
      );
    }
    const items = order.items || [];
    const qty = getTotalQty(items);
    return (
      <div className={styles.ordersTableAmount}>
        <strong>{order.total} ₴</strong>
        <span>{qty} {qty === 1 ? 'шт.' : 'шт.'}</span>
      </div>
    );
  };

  const renderBuyerCell = (order) => (
    <div className={styles.ordersTableBuyer}>
      <button type="button" className={styles.ordersTableBuyerLink} onClick={() => onEdit?.(order)}>
        {order.firstName} {order.lastName}
      </button>
      <div className={styles.ordersTableBuyerPhone}>{order.telephone}</div>
      {order.User?.email && (
        <div className={styles.ordersTableBuyerEmail}>{order.User.email}</div>
      )}
      <div className={styles.ordersTableBuyerRating}>Без рейтингу</div>
    </div>
  );

  const renderDeliveryCell = (order) => {
    const badge = getDeliveryBadge(order.status);
    return (
      <div className={styles.ordersTableDelivery}>
        <div className={styles.ordersTableCarrier}>
          <span>{getCarrierIcon(order.carrier)}</span>
          <span>{order.carrier}</span>
        </div>
        <div className={styles.ordersTableAddress}>
          {order.citi}, отд. {order.department}
        </div>
        {order.trackingNumber ? (
          <div className={styles.ordersTableTracking}>{order.trackingNumber}</div>
        ) : (
          <button type="button" className={styles.ordersTableAddTn} onClick={() => handleAddTracking(order)}>
            Додати ТТН
          </button>
        )}
        {badge && (
          <span className={`${styles.ordersTableDeliveryBadge} ${styles[`ordersTableDeliveryBadge_${badge.tone}`]}`}>
            {badge.text}
          </span>
        )}
        <div className={styles.ordersTablePayment}>{order.payment}</div>
        {order.isPaid && <span className={styles.ordersTablePaidBadge}>Оплачено</span>}
      </div>
    );
  };

  const renderStatusCell = (order) => {
    const status = order.status || 'new';
    const isUpdating = statusUpdating[order.id];
    return (
      <div className={styles.ordersTableStatusWrap}>
        <select
          className={`${styles.ordersTableStatusSelect} ${styles[`ordersTableStatus_${status}`]}`}
          value={status}
          disabled={isUpdating}
          onChange={(e) => handleStatusChange(order, e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
        <div className={styles.ordersTableRowActions}>
          <button type="button" className={styles.ordersTableEditBtn} onClick={() => onEdit?.(order)}>
            Змінити
          </button>
          <button type="button" className={styles.ordersTableDeleteBtn} onClick={() => onDelete?.(order)}>
            Видалити
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.ordersListPage}>
      <div className={styles.ordersListHeader}>
        <h2 className={styles.ordersListTitle}>Список замовлень</h2>
        <div className={styles.ordersListHeaderActions}>
          <button type="button" className={styles.ordersListExportBtn} onClick={handleExport}>
            Експорт в Excel
          </button>
          {onRefresh && (
            <button
              type="button"
              className={styles.ordersListRefreshBtn}
              onClick={onRefresh}
              disabled={loading}
            >
              {loading ? 'Завантаження…' : 'Оновити'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={`${styles.message} ${styles.error}`}>
          {typeof error === 'string' ? error : 'Не вдалося завантажити замовлення'}
        </div>
      )}

      <div className={styles.ordersListStats}>
        <div className={styles.ordersListStat}>
          <span className={styles.ordersListStatLabel}>Усіго</span>
          <strong>{stats.total}</strong>
        </div>
        <div className={styles.ordersListStat}>
          <span className={styles.ordersListStatLabel}>Нові</span>
          <strong className={styles.ordersListStatNew}>{stats.new}</strong>
        </div>
        <div className={styles.ordersListStat}>
          <span className={styles.ordersListStatLabel}>В обробці</span>
          <strong>{stats.processing}</strong>
        </div>
        <div className={styles.ordersListStat}>
          <span className={styles.ordersListStatLabel}>Виконані</span>
          <strong>{stats.completed}</strong>
        </div>
      </div>

      <div className={styles.ordersListToolbar}>
        <button type="button" className={styles.ordersListFilterBtn}>Фільтри</button>
        <div className={styles.ordersListSearch}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук за номером замовлення, ПІБ, телефоном, Email, номером накладної"
          />
        </div>
        <select
          className={styles.ordersListFilterSelect}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label} ({filterCounts[opt.id] ?? 0})
            </option>
          ))}
        </select>
      </div>

      {!orders.length ? (
        <div className={styles.empty}>Замовлення отсутствуют</div>
      ) : (
        <div className={styles.ordersTableWrap}>
          <table className={styles.ordersTable}>
            <thead>
              <tr>
                <th className={styles.ordersTableCheck}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th>Замовлення</th>
                <th>Сума</th>
                <th>Покупець</th>
                <th>Доставка и оплата</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className={styles.ordersTableEmpty}>
                    За вашим запитом нічого не знайдено
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const items = order.items || [];
                  const firstItem = items[0] || {};
                  const isExpanded = expandedOrders[order.id];
                  const hasMoreItems = items.length > 1;

                  return (
                    <React.Fragment key={order.id}>
                      <tr className={selected.includes(order.id) ? styles.ordersTableSelected : ''}>
                        <td className={styles.ordersTableCheck}>
                          <input
                            type="checkbox"
                            checked={selected.includes(order.id)}
                            onChange={() => toggleSelect(order.id)}
                          />
                        </td>
                        <td className={styles.ordersTableOrderCol}>
                          {renderOrderItemCell(firstItem, order)}
                          {hasMoreItems && (
                            <button
                              type="button"
                              className={styles.ordersTableExpandBtn}
                              onClick={() => toggleExpand(order.id)}
                            >
                              {items.length} {items.length === 1 ? 'товар' : items.length < 5 ? 'товари' : 'товарів'} у замовленні
                              <span className={isExpanded ? styles.ordersTableExpandOpen : ''}>▾</span>
                            </button>
                          )}
                        </td>
                        <td>{renderAmountCell(order)}</td>
                        <td>{renderBuyerCell(order)}</td>
                        <td>{renderDeliveryCell(order)}</td>
                        <td>{renderStatusCell(order)}</td>
                      </tr>
                      {isExpanded && items.slice(1).map((item, idx) => (
                        <tr key={`${order.id}-item-${idx}`} className={styles.ordersTableSubRow}>
                          <td />
                          <td className={styles.ordersTableOrderCol}>
                            {renderOrderItemCell(item, order, true)}
                          </td>
                          <td>{renderAmountCell(order, item)}</td>
                          <td />
                          <td />
                          <td />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.ordersListFooter}>
        Показано {filteredOrders.length} з {orders.length}
        {selected.length > 0 && ` · Обрано: ${selected.length}`}
      </div>
    </div>
  );
};

export default OrdersList;
